import * as React from 'react'
import { SettingsSectionWithTabs } from '../../components/SettingsSectionWithTabs'
import { FieldsetSwitch } from '../../components/FieldsetSwitch'
import { FieldsetSelect } from '../../components/FieldsetSelect'
import {
  useMutationViolationsConfiguration,
  useQueryViolationsConfiguration,
  ViolationConfigurationModel,
  ViolationConfigurationPayload,
} from '@/lib/api/company/configuration/violations'
import { OccasionalTransportMode } from '@/app/constants/configuration/occasionalTransportMode'
import { PlaceChangeAsWork } from '@/app/constants/configuration/placeChangeAsWork'

import { useWrapConfirm } from '@/modules/shared/hooks/useWrapConfirm'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { formatHours } from '@/app/components/configuration/renderer'

import { getOccasionalTransportModeName } from '@/app/common/converters/configuration/occasionalTransportModeToTextConverter'
import { getPlaceChangeAsWorkName } from '@/app/common/converters/configuration/placeChangeAsWorkToTextConverter'
import { FieldsetInput } from '../../components/FieldsetInput'
import { Button } from '@/ui'
import { SettingsPageWrapper } from '@/modules/settings/components/SettingsPageWrapper'
import { usePersistentTab } from '@/modules/shared/stores/tabsStore'

import { calculateViolation } from '@/lib/api/company/violation/calculate/calculateViolation'
import { useViolationCalculationTask } from '@/modules/shared/hooks/useViolationCalculationTask'
import { RouteComponentProps } from '@/app/navigation/withRouter'
import { ConfigurationType } from '@/app/constants/configuration/configurationType'
import { QUERY_KEY_SETTINGS_VIOLATION } from '@/lib/api/constants'

export const PLACE_CHANGES: Array<PlaceChangeAsWork> = [
  PlaceChangeAsWork.No,
  PlaceChangeAsWork.Always,
  PlaceChangeAsWork.OnlyNotManual,
  PlaceChangeAsWork.SkipFerryTrain,
]

export const OCCASIONAL_TRANSPORT_MODES: Array<OccasionalTransportMode> = [
  OccasionalTransportMode.Off,
  OccasionalTransportMode.ForBus,
  OccasionalTransportMode.On,
]

// Violations configuration page
export const ViolationPageContent = ({
  match,
}: RouteComponentProps<{ companyId: string }>) => {
  const companyId = match.params.companyId
  const readOnly = false
  const wrapConfirm = useWrapConfirm()
  const { t } = useLocalize()

  const diffLabels = React.useMemo<Record<string, string>>(() => ({
    minimalDailyRestDuration: t('configuration-violation.min-daily-rest'),
    occasionalTransportMode: t('configuration-violation.occasional-transport'),
    useGITDguidelines: t('configuration-violation.gitd-guidelines'),
    outOfScopeWork: t('configuration-violation.out-of-scope-work'),
    outOfScopeOneMinuteTolerance: t('configuration-violation.out-tolerance'),
    placeChangeAsWork: t('configuration-violation.place-change-as-work'),
    requierDrivingBeforeCrewWork: t('configuration-violation.require-crew-driving'),
    busServiceDeviation: t('configuration-violation.bus-service-deviation'),
    ignoreOneMinuteDrivingOnCrewDetection: t('configuration-violation.ignore-one-minute-driving'),
    treatAllFerryTrainRestAsRegularDailyRest: t('configuration-violation.ferry-rest-as-regular'),
    showNoDataActivityViolation: t('configuration-violation.show-no-data-violation'),
    showCompanyReputationIndex: t('configuration-violation.show-reputation-index'),
    maxSelectedDriversForAutomaticViolationCalculation: t('configuration-violation.auto-calculation'),
    minimalWeekRestDuration: t('configuration-violation.min-weekly-rest'),
    startPlaceHoursShift: t('configuration-violation.start-day-shift'),
    endPlaceHoursShift: t('configuration-violation.end-day-shift'),
    allowaEndPlaceOnDailyRest: t('configuration-violation.allow-end-place-on-rest'),
    minimalNoDataActivityCausingViolation: t('configuration-violation.min-no-data-period'),
    weeklyRestFerryTrainTolerance: t('configuration-violation.ferry-rest-tolerance'),
    covid19TimeExtension: t('configuration-violation.covid-extensions'),
    checkCountryOnMobilityPackageViolation: t('configuration-violation.check-country-on-violation'),
    forceRegularRestInMobilityPackage: t('configuration-violation.force-regular-rest-mobility'),
    mobilityPackageWeeklyRestsAllowActivityStartTime: t('configuration-violation.weekly-rest-all-subperiods'),
    requireValidCrewOnAvailabilityBreak: t('configuration-violation.require-valid-crew-break'),
    forceRegularRestForDaysWithNoDriving: t('configuration-violation.force-rest-no-driving-days'),
    recompenseWeekRestInFirstFreeSpace: t('configuration-violation.recompense-rest-first-space'),
    importActivitiesAsOUT: t('configuration-violation.import-gps-as-out'),
    violationsCostOptimalization: t('configuration-violation.optimize-penalty'),
    combineViolationPenalties: t('configuration-violation.combine-penalties'),
    allowWorkBreakInDaysWithDriving: t('configuration-violation.art13-uocpk'),
  }), [t])

  const diffFormatters = React.useMemo<Record<string, (v: unknown) => string>>(() => ({
    occasionalTransportMode: (v) => getOccasionalTransportModeName(v as OccasionalTransportMode, t),
    placeChangeAsWork: (v) => getPlaceChangeAsWorkName(v as PlaceChangeAsWork, t),
    minimalDailyRestDuration: (v) => formatHours(v as number, t),
  }), [t])

  const [selectedTab, setSelectedTab] = usePersistentTab(
    'settings-violations',
    'a',
  )

  const q = useQueryViolationsConfiguration({ companyId })
  const mutation = useMutationViolationsConfiguration(companyId)
  const [changes, setChanged] = React.useState<
    Partial<ViolationConfigurationPayload>
  >({})

  const state: ViolationConfigurationPayload = structuredClone({
    ...(q.data ?? {}),
    ...changes,
  } as ViolationConfigurationPayload)

  const isDirty = Object.values(changes).length > 0

  const handleChangeBoolean = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value, name, checked } = e.target
      setChanged((changed) => ({
        ...changed,
        [name]: checked,
      }))
    },
    [],
  )

  const handleSave = async () => {
    if (state) {
      await mutation.mutateAsync(state)
      setChanged({})
      wrapConfirm(handleCalculate, {
        title: t('driverViolations.calculate-violation-title'),
        message: t('driverViolations.configuration-calculate-violation-message'),
      })()
    }
  }

  const handleReset = React.useCallback(() => {
    setChanged({})
  }, [q.data])

  const handleSelect = (value: string, props: { name: string; option?: { key?: any } }) => {
    setChanged((state) => ({ ...state, [props.name]: props.option?.key ?? value }))
  }

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value, name } = e.target
      setChanged((changed) => ({
        ...changed,
        [name]: value,
      }))
    },
    [],
  )

  const handleChangeNumber = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value, name } = e.target
      setChanged((changed) => ({
        ...changed,
        [name]: value && Number(value),
      }))
    },
    [],
  )

  const minDailyRestOptions = React.useMemo(() => {
    if (
      !state ||
      !Array.isArray(state.minimalDailyRestDurationAvailableValues)
    ) {
      return []
    }
    const availableValues = state.minimalDailyRestDurationAvailableValues
    return availableValues.map((value) => ({
      key: value,
      value: value.toString(),
      label: formatHours(value, t),
    }))
  }, [state.minimalDailyRestDurationAvailableValues])

  const occasionalTransportModes = React.useMemo(() => {
    return OCCASIONAL_TRANSPORT_MODES.map((value) => ({
      key: value,
      value: value.toString(),
      label: getOccasionalTransportModeName(value, t),
    }))
  }, [])

  const startCalculation = React.useCallback(
    (requestId: string) =>
      calculateViolation({ companyId, payload: { id: requestId } }),
    [companyId],
  )
  // Bez blokady przycisku: przeliczenie jest tu proponowane po zapisie konfiguracji,
  // nie ma osobnego "Przelicz". Liczy sie samo przejecie 409 - inaczej zapis ustawien
  // konczyl sie komunikatem o bledzie, gdy ktos inny wlasnie przeliczal.
  const { calculate: handleCalculate } = useViolationCalculationTask({
    companyId,
    start: startCalculation,
  })

  return (
    <SettingsPageWrapper
      isReady={!!state}
      isPending={q.isFetching || mutation.isPending}
      isError={q.isError}
      isDirty={isDirty}
      restore={{
        configurationType: ConfigurationType.Violation,
        tabLabelKey: 'configuration-menu.violations',
        queryKeys: [QUERY_KEY_SETTINGS_VIOLATION],
        serverData: q.data,
        withDefaults: true,
      }}
      actions={
        <>
          <Button
            disabled={!isDirty}
            className="tw-rounded-lg tw-px-4 tw-text-content-secondary"
            variant="secondary"
            size="sm"
            onClick={wrapConfirm(handleReset, {
              message: t('configuration.reset-confirm'),
            })}
          >
            {t('common.reset')}
          </Button>
          <Button
            disabled={!isDirty}
            className="tw-rounded-lg tw-px-4"
            size="sm"
            onClick={wrapConfirm(handleSave, { message: t('configuration.save-confirm') })}
          >
            {t('common.save')}
          </Button>
        </>
      }
      errorMsg={t('configuration-violation.load-error')}
      globalErrorMsg={t('configuration-violation.load-error-global')}
      diffBefore={(q.data ?? {}) as Record<string, unknown>}
      diffAfter={{ ...(q.data ?? {}), ...changes } as Record<string, unknown>}
      diffLabels={diffLabels}
      diffFormatters={diffFormatters}
    >
      {state && (
        <SettingsSectionWithTabs
          title=""
          selected={selectedTab}
          setSelected={setSelectedTab}
        >
          <div
            data-tab-name="a"
            title={t('configuration-violation.tab-basic')}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            <FieldsetSelect
              label={t('configuration-violation.min-daily-rest')}
              name="minimalDailyRestDuration"
              options={minDailyRestOptions}
              value={String(state.minimalDailyRestDuration) || ''}
              handleChange={handleSelect}
              disabled={readOnly}
            />

            <FieldsetSelect
              label={t('configuration-violation.occasional-transport')}
              name="occasionalTransportMode"
              options={occasionalTransportModes}
              value={String(state.occasionalTransportMode)}
              handleChange={handleSelect}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.gitd-guidelines')}
              name="useGITDguidelines"
              checked={state.useGITDguidelines}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.out-of-scope-work')}
              name="outOfScopeWork"
              checked={state.outOfScopeWork}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.out-tolerance')}
              name="outOfScopeOneMinuteTolerance"
              checked={state.outOfScopeOneMinuteTolerance}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSelect
              label={t('configuration-violation.place-change-as-work')}
              name="placeChangeAsWork"
              options={PLACE_CHANGES.map((value) => ({
                key: value,
                value: value.toString(),
                label: getPlaceChangeAsWorkName(value, t),
              }))}
              value={String(state.placeChangeAsWork)}
              handleChange={handleSelect}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.require-crew-driving')}
              name="requierDrivingBeforeCrewWork"
              checked={state.requierDrivingBeforeCrewWork}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.bus-service-deviation')}
              name="busServiceDeviation"
              checked={state.busServiceDeviation}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.ignore-one-minute-driving')}
              name="ignoreOneMinuteDrivingOnCrewDetection"
              checked={state.ignoreOneMinuteDrivingOnCrewDetection}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.ferry-rest-as-regular')}
              name="treatAllFerryTrainRestAsRegularDailyRest"
              checked={state.treatAllFerryTrainRestAsRegularDailyRest}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.show-no-data-violation')}
              name="showNoDataActivityViolation"
              checked={state.showNoDataActivityViolation}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.show-reputation-index')}
              name="showCompanyReputationIndex"
              checked={state.showCompanyReputationIndex}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetInput
              label={t('configuration-violation.auto-calculation')}
              type="number"
              name="maxSelectedDriversForAutomaticViolationCalculation"
              value={
                state.maxSelectedDriversForAutomaticViolationCalculation?.toString() ||
                ''
              }
              onChange={handleChangeNumber}
              disabled={readOnly}
              min={0}
              max={99999}
              step={1}
              suffix={t('configuration-violation.drivers-suffix')}
            />

            <FieldsetInput
              label={t('configuration-violation.min-weekly-rest')}
              type="number"
              name="minimalWeekRestDuration"
              value={state.minimalWeekRestDuration?.toString() || ''}
              onChange={handleChangeNumber}
              disabled={readOnly}
              min={0}
              max={99999}
              step={1}
            />
          </div>
          <div
            data-tab-name="b"
            title={t('configuration-violation.tab-additional')}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            <FieldsetInput
              label={t('configuration-violation.start-day-shift')}
              type="time"
              name="startPlaceHoursShift"
              value={state.startPlaceHoursShift?.toString() || ''}
              onChange={handleChange}
              disabled={readOnly}
              min={0}
              max={99999}
              step={60}
            />

            <FieldsetInput
              label={t('configuration-violation.end-day-shift')}
              type="time"
              name="endPlaceHoursShift"
              value={state.endPlaceHoursShift?.toString() || ''}
              onChange={handleChange}
              disabled={readOnly}
              min={0}
              max={99999}
              step={60}
            />

            <FieldsetSwitch
              label={t('configuration-violation.allow-end-place-on-rest')}
              name="allowaEndPlaceOnDailyRest"
              checked={state.allowaEndPlaceOnDailyRest}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetInput
              label={t('configuration-violation.min-no-data-period')}
              type="time"
              name="minimalNoDataActivityCausingViolation"
              value={
                state.minimalNoDataActivityCausingViolation?.toString() || ''
              }
              onChange={handleChange}
              disabled={readOnly}
              min={0}
              max={99999}
              step={60}
            />

            <FieldsetInput
              label={t('configuration-violation.ferry-rest-tolerance')}
              type="time"
              name="weeklyRestFerryTrainTolerance"
              value={state.weeklyRestFerryTrainTolerance?.toString() || ''}
              onChange={handleChange}
              disabled={readOnly}
              min={0}
              max={99999}
              step={60}
            />

            <FieldsetSwitch
              label={t('configuration-violation.covid-extensions')}
              name="covid19TimeExtension"
              checked={state.covid19TimeExtension}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.check-country-on-violation')}
              name="checkCountryOnMobilityPackageViolation"
              checked={state.checkCountryOnMobilityPackageViolation}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.force-regular-rest-mobility')}
              name="forceRegularRestInMobilityPackage"
              checked={state.forceRegularRestInMobilityPackage}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.weekly-rest-all-subperiods')}
              name="mobilityPackageWeeklyRestsAllowActivityStartTime"
              checked={state.mobilityPackageWeeklyRestsAllowActivityStartTime}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.require-valid-crew-break')}
              name="requireValidCrewOnAvailabilityBreak"
              checked={state.requireValidCrewOnAvailabilityBreak}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.force-rest-no-driving-days')}
              name="forceRegularRestForDaysWithNoDriving"
              checked={state.forceRegularRestForDaysWithNoDriving}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.recompense-rest-first-space')}
              name="recompenseWeekRestInFirstFreeSpace"
              checked={state.recompenseWeekRestInFirstFreeSpace}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.import-gps-as-out')}
              name="importActivitiesAsOUT"
              checked={state.importActivitiesAsOUT}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.optimize-penalty')}
              name="violationsCostOptimalization"
              checked={state.violationsCostOptimalization}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.combine-penalties')}
              name="combineViolationPenalties"
              checked={state.combineViolationPenalties}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />

            <FieldsetSwitch
              label={t('configuration-violation.art13-uocpk')}
              name="allowWorkBreakInDaysWithDriving"
              checked={state.allowWorkBreakInDaysWithDriving}
              onChange={handleChangeBoolean}
              disabled={readOnly}
            />
          </div>
        </SettingsSectionWithTabs>
      )}
    </SettingsPageWrapper>
  )
}
